import { h, createContext } from 'preact'
import { useContext, useState, useCallback, useMemo } from 'preact/hooks'

import { useCurrentUser } from 'resources/auth'
import { useLocation } from 'resources/location'

import StyleishModal from 'components/StyleishModal'
import Button from 'components/Button'
import ButtonRow from 'components/ButtonRow'

import './index.sass'

const PUBLIC_HUB_PATH_RE = /^\/[^/]+\/published(\/|$)/

const GuestModeContext = createContext(null)

function isGuestEligibleRoute(pathname) {
  return PUBLIC_HUB_PATH_RE.test(pathname || '')
}

export function GuestModeProvider({ children }) {
  const currentUser = useCurrentUser()
  const location = useLocation()
  const [modalOpen, setModalOpen] = useState(false)

  const isGuest = !currentUser && isGuestEligibleRoute(location.pathname)

  const joinNowHref = useMemo(
    () => isGuest ? location.toSignup().toString() : null,
    [isGuest, location.pathname]
  )

  const promptJoin = useCallback(
    () => { if (isGuest) setModalOpen(true) },
    [isGuest]
  )

  const closeModal = useCallback(() => setModalOpen(false), [])

  const value = useMemo(
    () => ({ isGuest, joinNowHref, promptJoin }),
    [isGuest, joinNowHref, promptJoin]
  )

  return <GuestModeContext.Provider value={value}>
    {isGuest && <GuestBanner joinNowHref={joinNowHref} />}
    {children}
    <GuestJoinModal open={modalOpen} onClose={closeModal} joinNowHref={joinNowHref} />
  </GuestModeContext.Provider>
}

export function useGuestMode() {
  return useContext(GuestModeContext) || { isGuest: false, joinNowHref: null, promptJoin: () => {} }
}

function GuestBanner({ joinNowHref }) {
  return <div className="GuestBanner">
    <span className="GuestBanner-message">
      You are browsing in guest mode. To join the hub or interact,
    </span>
    <Button type="primary" size="sm" href={joinNowHref} className="GuestBanner-button">
      Join now
    </Button>
  </div>
}

function GuestJoinModal({ open, onClose, joinNowHref }) {
  return <StyleishModal {...{
    open,
    onClose,
    title: 'Sign up to interact',
    className: 'GuestJoinModal',
  }}>
    <p>Please sign up to interact with this hub.</p>
    <ButtonRow>
      <Button type="normal" onClick={onClose}>Cancel</Button>
      <Button type="primary" href={joinNowHref}>Join now</Button>
    </ButtonRow>
  </StyleishModal>
}
